import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../convex/_generated/api.js';
import { useAuth } from './AuthContext';
import { Id } from '../convex/_generated/dataModel';
import * as Sentry from '@sentry/react';

interface ConversationMember {
  userId: Id<'users'>;
  name?: string;
  email?: string;
}

interface RawConversation {
  _id: Id<'conversations'>;
  type?: string;
  membership?: {
    unreadCount?: number;
  };
  lastMessagePreview?: string;
  lastMessageAt?: number;
  updatedAt?: number;
  createdAt: number;
  lastMessageSenderType?: string;
  members?: ConversationMember[];
}

interface NormalizedConversation {
  id: Id<'conversations'>;
  type?: string;
  unreadCount: number;
  lastMessage: {
    content: string;
    timestamp: number;
    senderType: string;
  };
  otherMembers?: ConversationMember[];
  _id: Id<'conversations'>;
  membership?: {
    unreadCount?: number;
  };
  lastMessagePreview?: string;
  lastMessageAt?: number;
  updatedAt?: number;
  createdAt: number;
  lastMessageSenderType?: string;
  members?: ConversationMember[];
}

interface RawMessage {
  _id: Id<'messages'>;
  content: string;
  senderId: string;
  senderType: string;
  metadata?: Record<string, unknown>;
  timestamp: number;
  read: boolean;
}

interface NormalizedMessage {
  id: Id<'messages'>;
  content: string;
  senderId: string;
  senderType: string;
  metadata?: Record<string, unknown>;
  timestamp: number;
  read: boolean;
}

interface StartConversationParams {
  recipientId?: Id<'users'>;
  gameId?: Id<'games'>;
  libraryId?: Id<'libraries'>;
  borrowRequestId?: Id<'borrowRequests'>;
  paymentId?: Id<'payments'>;
  topic?: string;
  ownerId?: Id<'users'>;
  initialMessage?: string;
}

interface ContactSupportParams {
  subject: string;
  message: string;
  priority?: string;
}

interface MessageOwnerOptions {
  gameId?: Id<'games'>;
  libraryId?: Id<'libraries'>;
  borrowRequestId?: Id<'borrowRequests'>;
  paymentId?: Id<'payments'>;
  topic?: string;
  initialMessage?: string;
}

interface MessagingContextType {
  isAuthenticated: boolean;
  conversations: NormalizedConversation[];
  filteredConversations: NormalizedConversation[];
  friends: unknown[];
  activeConversation: NormalizedConversation | null;
  activeConversationId: Id<'conversations'> | null;
  activeConversationMessages: NormalizedMessage[];
  conversationFilter: string;
  setConversationFilter: React.Dispatch<React.SetStateAction<string>>;
  setActiveConversation: (
    conversationId: Id<'conversations'> | null
  ) => Promise<void>;
  sendMessage: (
    conversationId: Id<'conversations'>,
    content: string,
    metadata?: Record<string, unknown>
  ) => Promise<void>;
  startConversation: (params: StartConversationParams) => Promise<unknown>;
  messageOwner: (
    ownerId: Id<'users'>,
    options?: MessageOwnerOptions
  ) => Promise<unknown>;
  contactSupport: (params: ContactSupportParams) => Promise<unknown>;
  getTotalUnreadCount: () => number;
  sendFriendRequest: (friendId: Id<'users'>) => Promise<unknown>;
  sendFriendRequestByEmail: (email: string) => Promise<unknown>;
  sendFriendRequestByName: (name: string) => Promise<unknown>;
  searchUsersByName: (query: string) => Promise<unknown[]>;
  respondToFriendRequest: (
    requesterId: Id<'users'>,
    action: string
  ) => Promise<unknown>;
  removeFriend: (friendId: Id<'users'>) => Promise<unknown>;
}

const MessagingContext = createContext<MessagingContextType | undefined>(
  undefined
);

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

const normalizeConversations = (
  conversations: RawConversation[] = [],
  currentUserId?: Id<'users'>
): NormalizedConversation[] => {
  return conversations.map((conversation) => ({
    ...conversation,
    id: conversation._id,
    unreadCount: conversation.membership?.unreadCount || 0,
    lastMessage: {
      content: conversation.lastMessagePreview || 'Tap to view details',
      timestamp:
        conversation.lastMessageAt ||
        conversation.updatedAt ||
        conversation.createdAt,
      senderType: conversation.lastMessageSenderType || 'user',
    },
    otherMembers: conversation.members?.filter(
      (member) => member.userId !== currentUserId
    ),
  }));
};

const normalizeMessages = (messages: RawMessage[] = []): NormalizedMessage[] =>
  messages.map((message) => ({
    id: message._id,
    content: message.content,
    senderId: message.senderId,
    senderType: message.senderType,
    metadata: message.metadata,
    timestamp: message.timestamp,
    read: message.read,
  }));

export const MessagingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user, isAuthenticated } = useAuth();
  const currentUserId = user?._id;
  const [activeConversationId, setActiveConversationId] =
    useState<Id<'conversations'> | null>(null);
  const [conversationFilter, setConversationFilter] = useState<string>('all');

  const conversationsQuery = useQuery(
    api.conversations.listConversations,
    isAuthenticated ? {} : 'skip'
  );

  const friendsQuery = useQuery(
    api.friends.listFriends,
    isAuthenticated ? {} : 'skip'
  );

  const conversations = useMemo(
    () => normalizeConversations(conversationsQuery || [], currentUserId),
    [conversationsQuery, currentUserId]
  );

  const friends = friendsQuery || [];

  useEffect(() => {
    if (!isAuthenticated) {
      queueMicrotask(() => setActiveConversationId(null));
      return;
    }
  }, [isAuthenticated]);

  const activeConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => conversation.id === activeConversationId
      ) || null,
    [conversations, activeConversationId]
  );

  const conversationMessagesQuery = useQuery(
    api.conversations.getConversationMessages,
    isAuthenticated && activeConversationId
      ? { conversationId: activeConversationId }
      : 'skip'
  );

  const activeConversationMessages = useMemo(
    () => normalizeMessages(conversationMessagesQuery || []),
    [conversationMessagesQuery]
  );

  const sendMessageMutation = useMutation(api.conversations.sendMessage);
  const startConversationMutation = useMutation(
    api.conversations.startDirectConversation
  );
  const markConversationReadMutation = useMutation(
    api.conversations.markConversationRead
  );
  const openSupportTicketMutation = useMutation(
    api.conversations.openSupportTicket
  );

  const sendFriendRequestMutation = useMutation(api.friends.sendFriendRequest);
  const sendFriendRequestByEmailMutation = useMutation(
    api.friends.sendFriendRequestByEmail
  );
  const sendFriendRequestByNameMutation = useMutation(
    api.friends.sendFriendRequestByName
  );
  const searchUsersByNameAction = useAction(api.friends.searchUsersByName);
  const respondFriendRequestMutation = useMutation(
    api.friends.respondToFriendRequest
  );
  const removeFriendMutation = useMutation(api.friends.removeFriend);

  const setActiveConversation = useCallback(
    async (conversationId: Id<'conversations'> | null) => {
      setActiveConversationId(conversationId);
      if (conversationId) {
        try {
          await markConversationReadMutation({ conversationId });
        } catch (error: unknown) {
          console.error('Failed to mark conversation read:', error);
          Sentry.captureException(error);
        }
      }
    },
    [markConversationReadMutation]
  );

  const sendMessage = useCallback(
    async (
      conversationId: Id<'conversations'>,
      content: string,
      metadata?: Record<string, unknown>
    ) => {
      if (!content?.trim()) return;
      try {
        await sendMessageMutation({
          conversationId,
          content: content.trim(),
          metadata,
        });
      } catch (error: unknown) {
        console.error('Failed to send message:', error);
        Sentry.captureException(error);
      }
    },
    [sendMessageMutation]
  );

  const startConversation = useCallback(
    async ({
      recipientId,
      gameId,
      libraryId,
      borrowRequestId,
      paymentId,
      topic,
      ownerId,
      initialMessage,
    }: StartConversationParams) => {
      try {
        const conversation = await startConversationMutation({
          recipientId,
          gameId,
          libraryId,
          borrowRequestId,
          paymentId,
          topic,
          ownerId,
          initialMessage,
        });
        if (conversation?._id) {
          setActiveConversationId(conversation._id);
        }
        return conversation;
      } catch (error: unknown) {
        console.error('Failed to start conversation:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [startConversationMutation]
  );

  const contactSupport = useCallback(
    async ({ subject, message, priority }: ContactSupportParams) => {
      try {
        const { conversation } = await openSupportTicketMutation({
          subject,
          message,
          priority,
        });
        if (conversation?._id) {
          setActiveConversationId(conversation._id);
        }
        return conversation;
      } catch (error: unknown) {
        console.error('Failed to contact support:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [openSupportTicketMutation]
  );

  const messageOwner = useCallback(
    async (ownerId: Id<'users'>, options: MessageOwnerOptions = {}) => {
      if (!ownerId) {
        throw new Error('Owner ID is required to start a message');
      }
      return startConversation({
        recipientId: ownerId,
        ownerId,
        gameId: options.gameId,
        libraryId: options.libraryId,
        borrowRequestId: options.borrowRequestId,
        paymentId: options.paymentId,
        topic: options.topic,
        initialMessage: options.initialMessage,
      });
    },
    [startConversation]
  );

  const getTotalUnreadCount = useCallback(
    () =>
      conversations.reduce(
        (total, conversation) => total + (conversation.unreadCount || 0),
        0
      ),
    [conversations]
  );

  const filteredConversations = useMemo(() => {
    if (conversationFilter === 'bot') {
      return conversations.filter(
        (conversation) => conversation.type === 'bot'
      );
    }
    if (conversationFilter === 'support') {
      return conversations.filter(
        (conversation) => conversation.type === 'support'
      );
    }
    if (conversationFilter === 'direct') {
      return conversations.filter(
        (conversation) => conversation.type === 'direct'
      );
    }
    return conversations;
  }, [conversationFilter, conversations]);

  const sendFriendRequest = useCallback(
    async (friendId: Id<'users'>) => {
      try {
        return await sendFriendRequestMutation({ friendId });
      } catch (error: unknown) {
        console.error('Failed to send friend request:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [sendFriendRequestMutation]
  );

  const sendFriendRequestByEmail = useCallback(
    async (email: string) => {
      if (!email) return null;
      try {
        return await sendFriendRequestByEmailMutation({
          email,
        });
      } catch (error: unknown) {
        console.error('Failed to send friend request by email:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [sendFriendRequestByEmailMutation]
  );

  const sendFriendRequestByName = useCallback(
    async (name: string) => {
      if (!name) return null;
      try {
        return await sendFriendRequestByNameMutation({
          name,
        });
      } catch (error: unknown) {
        console.error('Failed to send friend request by name:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [sendFriendRequestByNameMutation]
  );

  const searchUsersByName = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) return [];
      try {
        return (
          (await searchUsersByNameAction({
            query,
          })) || []
        );
      } catch (error: unknown) {
        console.error('Failed to search users by name:', error);
        Sentry.captureException(error);
        return [];
      }
    },
    [searchUsersByNameAction]
  );

  const respondToFriendRequest = useCallback(
    async (requesterId: Id<'users'>, action: string) => {
      try {
        return await respondFriendRequestMutation({
          requesterId,
          action,
        });
      } catch (error: unknown) {
        console.error('Failed to respond to friend request:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [respondFriendRequestMutation]
  );

  const removeFriend = useCallback(
    async (friendId: Id<'users'>) => {
      try {
        return await removeFriendMutation({ friendId });
      } catch (error: unknown) {
        console.error('Failed to remove friend:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [removeFriendMutation]
  );

  const value = {
    isAuthenticated,
    conversations,
    filteredConversations,
    friends,
    activeConversation,
    activeConversationId,
    activeConversationMessages,
    conversationFilter,
    setConversationFilter,
    setActiveConversation,
    sendMessage,
    startConversation,
    messageOwner,
    contactSupport,
    getTotalUnreadCount,
    sendFriendRequest,
    sendFriendRequestByEmail,
    sendFriendRequestByName,
    searchUsersByName,
    respondToFriendRequest,
    removeFriend,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};
